import React from 'react';
import clsx from 'clsx';

export type ScrollEdgeSide = 'top' | 'bottom' | 'left' | 'right';

interface ComponentProps {
  edge: ScrollEdgeSide;
  visible: boolean;
  /** Moves the edge inwards, e.g. past a pinned table column that does not scroll. */
  inset?: number;
  className?: string;
  style?: React.CSSProperties;
}

const EDGE_CLASS: Record<ScrollEdgeSide, string> = {
  top: 'inset-x-0 top-0 h-6 flex-col bg-gradient-to-b',
  bottom: 'inset-x-0 bottom-0 h-6 flex-col-reverse bg-gradient-to-t',
  left: 'inset-y-0 left-0 w-6 flex-row bg-gradient-to-r',
  right: 'inset-y-0 right-0 w-6 flex-row-reverse bg-gradient-to-l',
};

/**
 * Marks content clipped by a scroll region: a brand rule on the edge with a soft tint fading
 * inwards, so a row or column cut in half reads as "there is more this way" rather than as broken.
 * Purely decorative — it must never intercept a click meant for the content beneath it.
 *
 * Place it inside a positioned parent that wraps the scrolling element, after that element, and show
 * it only while there is overflow on its side (see `useScrollOverflow`).
 */
const ScrollEdge: React.FC<ComponentProps> = ({ edge, visible, inset, className, style }) => (
  <div
    aria-hidden
    className={clsx(
      // translateZ(0): its own compositing layer, or the browser can stop drawing it over a scroll container (see ScrollEdgeArrow).
      'pointer-events-none absolute z-[1] flex [transform:translateZ(0)] transition-opacity duration-150 ease-in-out motion-reduce:transition-none',
      EDGE_CLASS[edge],
      'from-[rgba(254,80,0,0.14)] to-transparent',
      visible ? 'opacity-100' : 'opacity-0',
      className,
    )}
    style={{ ...(inset ? { [edge]: inset } : {}), ...style }}
  >
    <div className={clsx('shrink-0 bg-content-brand', edge === 'top' || edge === 'bottom' ? 'h-px w-full' : 'w-px h-full')} />
  </div>
);

export default ScrollEdge;
